import React, { useState, useEffect } from 'react';
import { projectService } from '../../../services/projectService';
import ProjectForm from '../../Components/Worldmap/ProjectForm';
import * as api from '../../../api';
import { supabase } from '../../../lib/supabaseClient';
import POSManager from './POSManager';
import DesignEventManager from './DesignEventManager';
import BoldBlue from '../../Atoms/BoldBlue';
import EmbeddedMapViewer from '../../Components/Worldmap/EmbeddedMapViewer';
import shoppingIcon from './SVG/2_SHOPPING.png';
import hotelIcon from './SVG/3_SONETTE.png';
import officeIcon from './SVG/8_OFFICE_STROKE.png';
import restaurantIcon from './SVG/5_CUISINE.png';
import culturalIcon from './SVG/6_PEINTURE.png';
import residenceIcon from './SVG/7_SERRURE.png';
import urbanIcon from './SVG/8_BANC.png';
import architectureIcon from './SVG/4_OFFICE.png';
import installationIcon from './SVG/9_INSTALLATION_SCENOGRAPHY.png';
import parisDesignWeekLogo from './SVG/paris_design_week_horizontal.png';

const POINT_ICONS = {
    "Retail": shoppingIcon,
    "Hotel": hotelIcon,
    "Office": officeIcon,
    "Restaurant/bar": restaurantIcon,
    "Cultural space": culturalIcon,
    "Private residence": residenceIcon,
    "Urban space/Transportation": urbanIcon,
    "Architecture": architectureIcon,
    "Installation/Scenography": installationIcon
};


const AdminUpload = ({ fullUser, fullUserLoading }) => {
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');
    const [projects, setProjects] = useState([]);
    const [editingProject, setEditingProject] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalProjects, setTotalProjects] = useState(0);
    const [isFormCollapsed, setIsFormCollapsed] = useState(true);
    const [activeTab, setActiveTab] = useState('projects');
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedItemForMap, setSelectedItemForMap] = useState(null);

    // Fetch projects on component mount
    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        const { count } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true });

        setTotalProjects(count);

        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setProjects(data);
        } catch (error) {
            setError('Erreur lors du chargement des projets : ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (projectId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
            try {
                setLoading(true);
                const { error } = await supabase
                    .from('projects')
                    .delete()
                    .eq('id', projectId);

                if (error) throw error;
                
                setFeedback('Projet supprimé avec succès');
                fetchProjects();
            } catch (error) {
                setError('Erreur lors de la suppression du projet : ' + error.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = async (project) => {
        setEditingProject(project);
        setIsEditing(true);
        setIsFormCollapsed(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setLoading(true);
            setFeedback('');
            setError('');

            try {
                await projectService.uploadCSV(file);
                setFeedback('Téléchargement réussi ! Fichier traité : ' + file.name);
                fetchProjects();
            } catch (error) {
                console.error('Upload error:', error);
                setError(`Échec du téléchargement : ${error.message}`);
            } finally {
                setLoading(false);
                e.target.value = '';
            }
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredProjects = projects
        .filter(project => {
            const searchLower = searchQuery.toLowerCase();
            return (
                project.nom_etablissement?.toLowerCase().includes(searchLower) ||
                project.adresse?.toLowerCase().includes(searchLower) ||
                project.titre_affiche?.toLowerCase().includes(searchLower) ||
                project.typologie?.toLowerCase().includes(searchLower)
            );
        })
        .sort((a, b) => {
            const aVal = a[sortField] || '';
            const bVal = b[sortField] || '';
            const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return sortDirection === 'asc' ? comparison : -comparison;
        });

    const tabStyle = (isActive) => ({
        padding: '16px 32px',
        backgroundColor: isActive ? '#0039d6' : 'transparent',
        color: isActive ? 'white' : '#666',
        border: 'none',
        borderRadius: '12px 12px 0 0',
        cursor: 'pointer',
        marginRight: '8px',
        fontWeight: isActive ? '600' : '400',
        fontSize: '16px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        top: isActive ? '2px' : '0',
        boxShadow: isActive ? '0 -4px 20px rgba(0, 57, 214, 0.2)' : 'none',
        transform: isActive ? 'scale(1.05)' : 'scale(1)',
        letterSpacing: '0.5px'
    });

    const styles = {
        container: {
            padding: '20px 40px',
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            maxWidth: '100%',
            overflowX: 'auto'
        },
        header: {
            marginBottom: '40px',
            textAlign: 'center'
        },
        title: {
            fontSize: '48px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #0039d6 0%, #0052ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '10px',
            letterSpacing: '-1px'
        },
        subtitle: {
            fontSize: '18px',
            color: '#6c757d',
            fontWeight: '400'
        },
        tabContainer: {
            marginBottom: '40px',
            borderBottom: '2px solid #e9ecef',
            paddingBottom: '0',
            display: 'flex',
            justifyContent: 'center'
        },
        contentContainer: {
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
            animation: 'fadeIn 0.6s ease-out',
            minWidth: '1200px',
            overflowX: 'auto'
        },
        statsCard: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '30px',
            borderRadius: '16px',
            marginBottom: '30px',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        statsNumber: {
            fontSize: '48px',
            fontWeight: '700',
            marginBottom: '8px'
        },
        statsLabel: {
            fontSize: '16px',
            opacity: '0.9'
        },
        uploadSection: {
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: '30px',
            borderRadius: '16px',
            marginBottom: '30px',
            border: '2px dashed #cbd5e0',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
        },
        formSection: {
            backgroundColor: '#f8f9fa',
            padding: '30px',
            borderRadius: '16px',
            marginBottom: '30px',
            border: '1px solid #e9ecef',
            transition: 'all 0.3s ease'
        },
        listContainer: {
            backgroundColor: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            minWidth: '1200px'
        },
        listHeader: {
            padding: '20px 30px',
            backgroundColor: '#f8f9fa',
            fontWeight: '600',
            color: '#495057',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '2px solid #e9ecef'
        },
        listItem: {
            padding: '25px 30px',
            borderBottom: '1px solid #e9ecef',
            alignItems: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#f8f9fa',
                transform: 'translateX(5px)'
            }
        },
        projectTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#212529',
            marginBottom: '5px'
        },
        projectSubtitle: {
            fontSize: '14px',
            color: '#6c757d'
        },
        addressText: {
            fontSize: '14px',
            color: '#495057',
            lineHeight: '1.5'
        },
        badge: {
            display: 'inline-block',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: '#e3f2fd',
            color: '#1976d2'
        },
        button: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            marginRight: '8px'
        },
        editButton: {
            backgroundColor: '#0039d6',
            color: 'white',
            '&:hover': {
                backgroundColor: '#0052ff',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 57, 214, 0.3)'
            }
        },
        deleteButton: {
            backgroundColor: '#dc3545',
            color: 'white',
            '&:hover': {
                backgroundColor: '#c82333',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
            }
        },
        searchBar: {
            width: '100%',
            padding: '16px 24px',
            border: '2px solid #e9ecef',
            borderRadius: '12px',
            fontSize: '16px',
            transition: 'all 0.3s ease',
            marginBottom: '30px',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            '&:focus': {
                outline: 'none',
                borderColor: '#0039d6',
                boxShadow: '0 0 0 3px rgba(0, 57, 214, 0.1)'
            }
        },
        emptyState: {
            textAlign: 'center',
            padding: '80px 20px',
            color: '#6c757d'
        },
        alert: {
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            animation: 'slideIn 0.3s ease-out'
        },
        successAlert: {
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb'
        },
        errorAlert: {
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb'
        }
    };

    // Add keyframes for animations
    const styleSheet = document.styleSheets[0];
    if (styleSheet && !document.getElementById('worldmap-animations')) {
        const animationStyles = document.createElement('style');
        animationStyles.id = 'worldmap-animations';
        animationStyles.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
            }
            @keyframes slideIn {
                from { opacity: 0; transform: translateX(-20px); }
                to { opacity: 1; transform: translateX(0); }
            }
            .list-item-hover:hover {
                background-color: #f8f9fa;
                transform: translateX(5px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            }
            .list-item-hover:hover .project-image {
                transform: scale(1.05);
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
            }
            .project-image {
                transition: all 0.3s ease;
            }
            .button-hover:hover {
                transform: translateY(-2px);
            }
            .search-focus:focus {
                outline: none;
                border-color: #0039d6;
                box-shadow: 0 0 0 3px rgba(0, 57, 214, 0.1);
            }
            
            /* Desktop and Tablet Responsive Design */
            @media (min-width: 768px) {
                .responsive-row, .responsive-header {
                    grid-template-columns: 80px 2fr 2fr 100px 160px !important;
                    min-height: 120px;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
            }
            
            /* Tablet adjustments */
            @media (min-width: 768px) and (max-width: 1200px) {
                .responsive-row, .responsive-header {
                    grid-template-columns: 70px 2fr 2fr 80px 140px !important;
                    gap: 15px !important;
                    padding: 15px !important;
                    min-height: 110px;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
                .project-image {
                    width: 70px !important;
                    height: 70px !important;
                }
                .icon-button {
                    width: 36px !important;
                    height: 36px !important;
                    font-size: 14px !important;
                }
                .map-button {
                    width: 80px !important;
                    height: 28px !important;
                    font-size: 10px !important;
                }
            }
            
            /* Large Desktop optimizations */
            @media (min-width: 1200px) {
                .responsive-row, .responsive-header {
                    grid-template-columns: 90px 2.5fr 2fr 120px 170px !important;
                    gap: 25px !important;
                    padding: 25px !important;
                    min-height: 130px;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
                .project-image {
                    width: 90px !important;
                    height: 90px !important;
                }
                .icon-button {
                    width: 44px !important;
                    height: 44px !important;
                    font-size: 18px !important;
                }
                .map-button {
                    width: 110px !important;
                    height: 36px !important;
                    font-size: 12px !important;
                }
            }
            
            /* Ultra-wide Desktop */
            @media (min-width: 1600px) {
                .responsive-row, .responsive-header {
                    grid-template-columns: 100px 3fr 2.5fr 140px 180px !important;
                    gap: 30px !important;
                    padding: 30px !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
                .project-image {
                    width: 100px !important;
                    height: 100px !important;
                }
                .icon-button {
                    width: 48px !important;
                    height: 48px !important;
                    font-size: 20px !important;
                }
                .map-button {
                    width: 120px !important;
                    height: 40px !important;
                    font-size: 13px !important;
                }
            }
            
            /* Container responsiveness */
            @media (min-width: 768px) and (max-width: 1200px) {
                .admin-container {
                    padding: 15px 20px !important;
                }
                .admin-content {
                    padding: 25px !important;
                    min-width: 900px !important;
                }
            }
            
            @media (min-width: 1200px) {
                .admin-container {
                    padding: 20px 40px !important;
                }
                .admin-content {
                    padding: 40px !important;
                    min-width: 1200px !important;
                }
            }
            
            @media (min-width: 1600px) {
                .admin-content {
                    min-width: 1400px !important;
                }
            }
            
            /* Ensure text doesn't overflow */
            .responsive-text {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 100%;
            }
            
            /* Icon button enhancements */
            .icon-button {
                transition: all 0.2s ease !important;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                border: none !important;
                cursor: pointer !important;
            }
            
            .icon-button:hover {
                transform: translateY(-2px) scale(1.05) !important;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
            }
            
            .icon-button:active {
                transform: translateY(0) scale(0.95) !important;
                transition: all 0.1s ease !important;
            }
        `;
        document.head.appendChild(animationStyles);
    }

    return (
        <div style={styles.container} className="admin-container">
            <div style={styles.header}>
                <h1 style={styles.title}>Administration Worldmap</h1>
                <p style={styles.subtitle}>Gérez les projets, points de vente et design events visibles sur la carte.</p>
            </div>
            
            <div style={styles.tabContainer}>
                <button 
                    style={tabStyle(activeTab === 'projects')}
                    onClick={() => setActiveTab('projects')}
                >
                    <span style={{ marginRight: '8px' }}>📍</span>
                    Projets ({totalProjects})
                </button>
                <button 
                    style={tabStyle(activeTab === 'pos')}
                    onClick={() => setActiveTab('pos')}
                >
                    <span style={{ marginRight: '8px' }}>🏪</span>
                    Points de Vente
                </button>
                <button 
                    style={tabStyle(activeTab === 'events')}
                    onClick={() => setActiveTab('events')}
                >
                    <span style={{ marginRight: '8px' }}>🎨</span>
                    Événements Design
                </button>
            </div>

            {activeTab === 'projects' && (
                <div style={styles.contentContainer} className="admin-content">
                    {/* Embedded Map Viewer */}
                    {selectedItemForMap && (
                        <EmbeddedMapViewer 
                            item={selectedItemForMap}
                            type="project"
                            onClose={() => setSelectedItemForMap(null)}
                        />
                    )}

                    {/* Statistics Card */}
                    <div style={styles.statsCard}>
                        <div>
                            <div style={styles.statsNumber}>{totalProjects}</div>
                            <div style={styles.statsLabel}>Total des Projets sur la Worldmap</div>
                        </div>
                        <div style={{ fontSize: '60px', opacity: '0.3' }}>🗺️</div>
                    </div>

                    {/* CSV Upload Section */}
                    <div 
                        style={styles.uploadSection}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <h3 style={{ marginBottom: '20px', color: '#495057' }}>
                            <span style={{ marginRight: '10px' }}>📁</span>
                            Télécharger des Projets via CSV
                        </h3>
                        <div style={{ marginBottom: '20px', color: '#6c757d' }}>
                            <p>Téléchargez un fichier CSV avec les colonnes suivantes :</p>
                            <pre style={{ 
                                background: 'white', 
                                padding: '15px', 
                                borderRadius: '8px',
                                fontSize: '13px',
                                overflow: 'auto',
                                border: '1px solid #e9ecef'
                            }}>
nom_etablissement, fiche_agence, titre_affiche, adresse,
interior_designers, points_of_interest, design_events,
design_points_of_sales, annee_projet, description_projet,
date_vernissage, heure_vernissage, date_debut_expo,
date_fin_expo, site_web, credits_photo, photos_projet,
longitude, latitude
                            </pre>
                        </div>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            disabled={loading}
                            style={{ marginBottom: '10px' }}
                            id="csv-upload"
                        />
                        <label 
                            htmlFor="csv-upload"
                            style={{
                                ...styles.button,
                                backgroundColor: '#28a745',
                                color: 'white',
                                display: 'inline-block',
                                cursor: 'pointer'
                            }}
                        >
                            Choisir un Fichier
                        </label>
                    </div>

                    {/* Feedback Messages */}
                    {loading && (
                        <div style={{ ...styles.alert, backgroundColor: '#cfe2ff', color: '#084298', border: '1px solid #b6d4fe' }}>
                            <span style={{ marginRight: '10px' }}>⏳</span> Traitement en cours...
                        </div>
                    )}
                    {feedback && (
                        <div style={{ ...styles.alert, ...styles.successAlert }}>
                            <span style={{ marginRight: '10px' }}>✅</span> {feedback}
                        </div>
                    )}
                    {error && (
                        <div style={{ ...styles.alert, ...styles.errorAlert }}>
                            <span style={{ marginRight: '10px' }}>⚠️</span> {error}
                        </div>
                    )}

                    {/* Add/Edit Project Form */}
                    {!isEditing && (
                        <div style={styles.formSection}>
                            <div 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    cursor: 'pointer',
                                    marginBottom: '20px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.3s ease',
                                    '&:hover': { backgroundColor: '#e9ecef' }
                                }}
                                onClick={() => setIsFormCollapsed(!isFormCollapsed)}
                            >
                                <span style={{ 
                                    marginRight: '15px', 
                                    fontSize: '24px',
                                    transform: isFormCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                    transition: 'transform 0.3s ease',
                                    display: 'inline-block'
                                }}>
                                    ▶
                                </span>
                                <h3 style={{ margin: 0, color: '#212529' }}>
                                    <span style={{ marginRight: '10px' }}>➕</span>
                                    Ajouter un Nouveau Projet
                                </h3>
                            </div>
                            {!isFormCollapsed && (
                                <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
                                    <ProjectForm 
                                        onSuccess={() => {
                                            fetchProjects();
                                            setFeedback('Projet ajouté avec succès ! 🎉');
                                            setIsFormCollapsed(true);
                                        }}
                                        onError={(error) => setError(error.message)}
                                        isEditing={false}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {isEditing && editingProject && (
                        <div style={styles.formSection}>
                            <div style={{ marginBottom: '20px' }}>
                                <button 
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditingProject(null);
                                    }}
                                    style={{
                                        ...styles.button,
                                        backgroundColor: '#6c757d',
                                        color: 'white'
                                    }}
                                    className="button-hover"
                                >
                                    ← Retour à la Liste
                                </button>
                            </div>
                            <h3 style={{ marginBottom: '20px', color: '#212529' }}>
                                <span style={{ marginRight: '10px' }}>✏️</span>
                                Modifier le Projet
                            </h3>
                            <ProjectForm 
                                initialData={editingProject}
                                isEditing={true}
                                onSuccess={() => {
                                    fetchProjects();
                                    setIsEditing(false);
                                    setEditingProject(null);
                                    setFeedback('Projet mis à jour avec succès ! ✨');
                                }}
                                onError={(error) => setError(error.message)}
                            />
                        </div>
                    )}

                    {/* Projects List */}
                    <div style={{ marginTop: '40px' }}>
                        <h3 style={{ marginBottom: '30px', fontSize: '24px', fontWeight: '600', color: '#212529' }}>
                            Projets Existants
                        </h3>
                        
                        {/* Search Bar */}
                        <input
                            type="text"
                            placeholder="🔍 Rechercher par nom, adresse ou type..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={styles.searchBar}
                            className="search-focus"
                        />

                        {/* Projects Table */}
                        <div style={styles.listContainer}>
                            {/* Table Header */}
                            <div style={{
                                ...styles.listHeader,
                                display: 'grid',
                                gridTemplateColumns: '80px 2fr 2fr 100px 160px',
                                gap: '20px',
                                alignItems: 'center',
                                padding: '20px',
                                width: '100%',
                                boxSizing: 'border-box'
                            }} className="responsive-header">
                                <div style={{ textAlign: 'center', fontWeight: '700' }}>Image</div>
                                <div style={{ cursor: 'pointer', fontWeight: '700' }} onClick={() => handleSort('nom_etablissement')}>
                                    Infos Projet {sortField === 'nom_etablissement' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </div>
                                <div style={{ cursor: 'pointer', fontWeight: '700' }} onClick={() => handleSort('adresse')}>
                                    Adresse {sortField === 'adresse' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </div>
                                <div style={{ cursor: 'pointer', fontWeight: '700', textAlign: 'center' }} onClick={() => handleSort('annee_projet')}>
                                    Année {sortField === 'annee_projet' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </div>
                                <div style={{ fontWeight: '700', textAlign: 'center' }}>Actions</div>
                            </div>

                            {/* Table Body */}
                            {filteredProjects.length === 0 ? (
                                <div style={styles.emptyState}>
                                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
                                    <h4>Aucun projet trouvé</h4>
                                    <p>Essayez d'ajuster vos critères de recherche</p>
                                </div>
                            ) : (
                                filteredProjects.map(project => (
                                    <div 
                                        key={project.id} 
                                        style={{
                                            ...styles.listItem,
                                            display: 'grid',
                                            gridTemplateColumns: '80px 2fr 2fr 100px 160px',
                                            gap: '20px',
                                            alignItems: 'center',
                                            minHeight: '120px',
                                            padding: '20px',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}
                                        className="list-item-hover responsive-row"
                                    >
                                        {/* Project Image */}
                                        <div style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            backgroundColor: '#f0f0f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                            flexShrink: 0
                                        }}
                                        className="project-image">
                                            {project.photos_projet ? (
                                                <img
                                                    src={project.photos_projet.split(',')[0].trim()}
                                                    alt="Projet"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div style={{
                                                display: project.photos_projet ? 'none' : 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                height: '100%',
                                                fontSize: '24px',
                                                color: '#999'
                                            }}>
                                                🏗️
                                            </div>
                                        </div>
                                        
                                        {/* Project Info */}
                                        <div style={{ 
                                            minWidth: 0, 
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                ...styles.projectTitle,
                                                marginBottom: '6px'
                                            }}
                                            className="responsive-text">
                                                {project.titre_affiche || project.nom_etablissement || 'Projet Sans Titre'}
                                            </div>
                                            {project.nom_etablissement && project.nom_etablissement !== project.titre_affiche && (
                                                <div style={styles.projectSubtitle}
                                                className="responsive-text">
                                                    {project.nom_etablissement}
                                                </div>
                                            )}
                                            {/* Point of Interest with Icon */}
                                            {project.points_of_interest && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    marginTop: '8px'
                                                }}>
                                                    {POINT_ICONS[project.points_of_interest] && (
                                                        <img 
                                                            src={POINT_ICONS[project.points_of_interest]} 
                                                            alt={project.points_of_interest}
                                                            style={{
                                                                width: '16px',
                                                                height: '16px',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                    )}
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#6c757d',
                                                        fontWeight: '500',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {project.points_of_interest}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Address */}
                                        <div style={styles.addressText}
                                        className="responsive-text">
                                            {project.adresse || '—'}
                                        </div>
                                        
                                        {/* Year */}
                                        <div style={{ 
                                            color: '#495057',
                                            textAlign: 'center',
                                            fontWeight: '500'
                                        }}>
                                            {project.annee_projet || '—'}
                                        </div>
                                        
                                        {/* Actions */}
                                        <div style={{ 
                                            display: 'flex', 
                                            gap: '8px',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            flexDirection: 'column'
                                        }}>
                                            <button
                                                onClick={() => {
                                                    setSelectedItemForMap(project);
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                style={{ 
                                                    ...styles.button, 
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    width: '100px',
                                                    height: '32px',
                                                    padding: '0 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '20px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    gap: '4px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    marginBottom: '8px'
                                                }}
                                                className="button-hover icon-button map-button"
                                                title="Voir sur la map"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                                                }}
                                            >
                                                🌍 Map
                                            </button>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleEdit(project)}
                                                style={{ 
                                                    ...styles.button, 
                                                    ...styles.editButton,
                                                    width: '40px',
                                                    height: '40px',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '8px',
                                                    fontSize: '18px',
                                                    color: 'white',
                                                    fontWeight: 'normal'
                                                }}
                                                className="button-hover icon-button"
                                                title="Modifier ce projet"
                                            >
                                                ✎
                                            </button>
                                            <button
                                                onClick={() => handleDelete(project.id)}
                                                style={{ 
                                                    ...styles.button, 
                                                    ...styles.deleteButton,
                                                    width: '40px',
                                                    height: '40px',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '8px',
                                                    fontSize: '18px',
                                                    color: 'white',
                                                    fontWeight: 'normal'
                                                }}
                                                className="button-hover icon-button"
                                                title="Supprimer ce projet"
                                            >
                                                ×
                                            </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Points of Sale Tab Content */}
            {activeTab === 'pos' && (
                <div style={styles.contentContainer}>
                    <POSManager />
                </div>
            )}

            {/* Design Events Tab Content */}
            {activeTab === 'events' && (
                <div style={styles.contentContainer}>
                    <DesignEventManager />
                </div>
            )}
        </div>
    );
};


export default AdminUpload;